import { useEffect } from 'react'

/** Static descriptive copy per consultation topic — presentational content
 * only (like AstrologerFaqSection); everything astrologer-specific in the
 * modal comes from the live offering/astro record. */
const TOPIC_DETAILS = {
  'Vedic Astrology': {
    icon: '🪐',
    points: [
      'Full birth-chart reading using classical Parashari principles',
      'Current dasha and transit analysis for timing decisions',
      'Guidance on career, relationships, health, and finances',
      'Practical remedies matched to your chart',
    ],
  },
  'Kundli Analysis': {
    icon: '📜',
    points: [
      'Detailed study of planetary positions and houses in your kundli',
      'Identification of doshas and their real-life impact',
      'Dasha periods — what to expect and when',
      'Remedies and timing guidance for important decisions',
    ],
  },
  'Vastu Shastra': {
    icon: '🏠',
    points: [
      'Directional analysis of your home or workspace',
      'Identifying energy imbalances affecting health and prosperity',
      'Practical corrections — no demolition needed',
      'Room-by-room placement guidance',
    ],
  },
  Palmistry: {
    icon: '✋',
    points: [
      'Reading of major and minor lines on both palms',
      'Mounts and finger analysis for personality insights',
      'Indications on career, relationships, and longevity',
      'How your palm lines evolve with your choices',
    ],
  },
  Numerology: {
    icon: '🔢',
    points: [
      'Life-path and destiny numbers from your birth date',
      'Name-number analysis and correction suggestions',
      'Lucky numbers, dates, and colours for key decisions',
      'Compatibility insights through numbers',
    ],
  },
  'Vedic Remedies': {
    icon: '📿',
    points: [
      'Remedies prescribed strictly from your birth chart',
      'Mantras, pujas, and fasting guidance',
      'Lifestyle adjustments grounded in classical texts',
      'Only what is truly needed — nothing more',
    ],
  },
  'Gem Therapy': {
    icon: '💎',
    points: [
      'Gemstone recommendation based on your planetary chart',
      'Correct weight, metal, and finger for wearing',
      'Energisation and first-wearing muhurta guidance',
      'When a gemstone should not be worn',
    ],
  },
  'Tarot Reading': {
    icon: '🔮',
    points: [
      'Focused card spreads for your specific questions',
      'Insights on love, career, and life transitions',
      'Clarity on the energies around your situation',
      'Actionable guidance from every card drawn',
    ],
  },
}

const genericDetails = (label) => ({
  icon: '✨',
  points: [
    `Personalised ${label.toLowerCase()} reading based on your details`,
    'Clear, practical guidance for your questions',
    'Simple remedies and next steps where needed',
    'Dedicated time for your own questions',
  ],
})

export default function OfferingDetailsModal({ offering, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!offering) return null
  const details = TOPIC_DETAILS[offering.label] ?? genericDetails(offering.label)

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center px-4 sm:px-6 py-8 overflow-y-auto bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${offering.title} details`}
        className="w-full max-w-lg my-auto rounded-2xl overflow-hidden bg-white border border-[#D7C29A]/40 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 w-full bg-linear-to-r from-[#C8922A] via-[#E6C878] to-[#C8922A]" />
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl" style={{ background: 'rgba(200,146,42,.12)' }}>
              {details.icon}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#8B7355] hover:text-[#1C140A] hover:bg-[#D7C29A]/20 transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="meta-pill text-[12px]!">{offering.label}</span>
            <span className="text-[#C8922A] font-semibold text-sm">★ {offering.rating}</span>
          </div>
          <h3 className="mt-3 font-cinzel text-xl text-[#1C140A]">{offering.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[#5C3A18]">{offering.desc}</p>

          <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B7355]">What this session covers</p>
          <ul className="mt-3 space-y-2.5">
            {details.points.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-[#5C3A18]">
                <span className="text-[#C8922A] mt-0.5">✦</span>
                {point}
              </li>
            ))}
          </ul>

          {offering.duration && (
            <p className="mt-6 text-xs text-[#8B7355]">
              ⏱ Approx. <span className="font-semibold text-[#1C140A]">{offering.duration} min</span> per session
            </p>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-7 w-full inline-flex items-center justify-center rounded-full px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] border transition-colors hover:bg-[#C8922A]/10"
            style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
